import { checkCustomizedVersion } from '@/libs/tools';
import i18n from '@/locales';
import { computed, getCurrentInstance } from '@vue/composition-api';
import { Modal } from 'view-design';
import useI18n from './useI18n';
import useStore from './useStore';
import useRouter from './useRouter';
import config from '@/config';

interface PackageInfoV5 {
    basic: string; // 免费版
    pro_v1: string; // Pro版
    max: string; // Max版
}

interface PackageInfo extends PackageInfoV5 {
    professional: string; // 专业版
    commercial: string; // 商业版
    corporate: string; // 定制版
    pro: string; // 高级版
    vip: string; // vip版
    enterprise: string; // 企业版
}

// 2025 新增版本
export const packageV5 = (suffix: string = ''): { [K in keyof PackageInfoV5]: string } => {
    return {
        basic: `Free${suffix}`,
        pro_v1: `Pro${suffix}`,
        max: `Max${suffix}`,
    };
};

export default function usePackage() {
    const instance = getCurrentInstance();

    const { route, router } = useRouter();

    const { siteConfig } = config;

    const { projectId, userModule } = useStore();
    const { isExpired, packageType } = userModule.useState(['isExpired', 'packageType']);
    const {
        isPackageV1,
        isPackageV2,
        isPackageV3,
        isPackageV5,
    } = userModule.useGetters([
        'isPackageV1',
        'isPackageV2',
        'isPackageV3',
        'isPackageV5',
    ]);

    const { renderKey, local, isZH } = useI18n();

    // 版本信息
    const packageInfo = computed(() => {
        const info: any = (i18n.messages[local.value].base as any)?.packageInfo || {};
        const packageText = isZH.value ? i18n.t('base.packageText').toString() : '';
        return {
            ...info,
            ...packageV5(packageText),
        };
    });

    const isCustomizedVersion = computed(() => {
        return checkCustomizedVersion();
    });

    const isCaiNiao = computed(() => {
        return siteConfig.brand && ['cninbox'].includes(siteConfig.key || '');
    });

    // 没有图标32*32图标的
    const notNoticeIcon = computed(() => {
        return ['sunsesoft', 'roosterchat'].includes(siteConfig.title);
    });

    const isPkgExpired = computed(() => isExpired.value === '1');

    const isBasic = computed(() => packageType.value === 'basic');

    const currentUpgradePackage = ['pro_v1', 'max'];

    const currentUpgradePackageText = computed(() => {
        const texts: string[] = [];
        currentUpgradePackage.forEach((item: string) => {
            texts.push(packageInfo.value[item]);
        });
        return texts.join('/');
    });

    // 是否 V2 付费项目
    const isV2PayPackage = computed(() => {
        return ['professional', 'commercial'].includes(packageType.value);
    });

    function checkDataAnalysisEnabled(type?: string) {
        // 商业版  企业版 支持
        // const flag = ['commercial', 'corporate'].includes(packageType.value);
        const flag = !['basic'].includes(packageType.value);
        return flag && type === 'dataAnalysis';
    }

    function showErrorTips(code: number | string) {
        Modal.warning({
            title: renderKey('tipsText'),
            content: renderKey(`errorCode.${code}`),
        });
    }

    function checkIsExpired() {
        if (isPkgExpired.value) {
            showErrorTips(4007);
            return true;
        }

        return false;
    }

    function checkIsCommercialLimit(auth: string) {
        if (checkIsExpired()) {
            return true;
        }
        if (!instance?.proxy.$checkAuth(auth)) {
            Modal.confirm({
                title: i18n.t('tipsText').toString(),
                content: i18n.t('errorCode.commercialLimit').toString(),
                okText: i18n.t('base.upgrade').toString(),
                cancelText: i18n.t('base.closeBtn').toString(),
                onOk() {
                    window.open('/fee?changePackage=1');
                },
            });
            return true;
        }

        return false;
    }

    function checkIsProLimit(auth: string) {
        if (checkIsExpired()) {
            return true;
        }
        if (!instance?.proxy.$checkAuth(auth)) {
            Modal.confirm({
                title: i18n.t('tipsText').toString(),
                content: i18n.t('errorCode.proLimit').toString(),
                okText: i18n.t('base.upgrade').toString(),
                cancelText: i18n.t('base.closeBtn').toString(),
                onOk() {
                    window.open('/fee?changePackage=1');
                },
            });
            return true;
        }

        return false;
    }

    // 添加账号成功后，但开启失败时， 检查权限
    function checkIsProLimitAfterAddAccount(auth: string) {
        if (checkIsExpired()) {
            return true;
        }
        if (!instance?.proxy.$checkAuth(auth)) {
            return true;
        }

        return false;
    }

    // 判断是否能够导出联系人
    function checkExportVistor() {
        // 基础版不支持
        const flag = !['basic'].includes(packageType.value);

        if (!flag) {
            showErrorTips('export');
            return false;
        }

        return true;
    }

    // 判断是否能够导出会话
    function checkExportSession() {
        // 商业版  企业版 支持
        const flag = !['basic'].includes(packageType.value);

        if (!flag) {
            showErrorTips('export');
            return false;
        }

        return true;
    }

    // 判断是否能够质检
    function checkSessionQuality() {
        // 商业版  企业版 支持
        const flag = !['basic'].includes(packageType.value);

        if (!flag) {
            showErrorTips('quality');
            return false;
        }

        return true;
    }

    const packageConfirm = (content:string, query: {[k:string]:string}) => {
        Modal.confirm({
            title: renderKey('base.tipText'),
            content,
            okText: renderKey('base.upgradeNow'),
            cancelText: renderKey('base.closeBtn'),
            onOk: () => {
                if (route.name === 'fee') {
                    router.replace({
                        name: 'fee',
                        query,
                    });
                } else {
                    router.push({
                        name: 'fee',
                        query,
                    });
                }
            },
        });
    };

    /**
     * 判断功能是否可用（非基础版）
     * @param callback
     * @param type：提示升级文案：basics、translate_num、send_email_num、infiniteQuantity
     */
    const checkV3FunctionPackage = (callback: any, type = 'basics') => {
        // 判断是否基础版
        if (['basic'].includes(packageType.value)) {
            const content = renderKey(`setting.fee.changePackageTips.${type}`, { packageText: currentUpgradePackageText.value });
            const query = {
                changePackage: '2',
            };
            packageConfirm(content, query);
            return;
        }
        callback();
    };

    /**
     * 判断功能是否可用（非基础版）—— 权限code判断
     * @param callback
     * @param type：提示升级文案：basics、translate_num、send_email_num、infiniteQuantity
     */
    const checkV3FunctionPackageIsLimit = (auth: string, callback: any, type = 'basics') => {
        if (checkIsExpired()) {
            return false;
        }
        // V2付费项目均开放该功能
        if (isV2PayPackage.value) {
            if (callback) {
                callback();
            }
            return true;
        }
        if (!instance?.proxy.$checkAuth(auth)) {
            Modal.confirm({
                title: renderKey('base.tipText'),
                content: renderKey(`setting.fee.changePackageTips.${type}`, { packageText: currentUpgradePackageText.value }),
                okText: renderKey('base.upgradeNow'),
                cancelText: renderKey('base.closeBtn'),
                onOk: () => {
                    if (type === 'channel') {
                        window.open('/fee');
                        return;
                    }
                    if (route.name !== 'fee') {
                        router.push({ name: 'fee' });
                    }
                },
            });
            return false;
        }
        if (callback) {
            callback();
        }
        return true;
    };

    const upgradePackageTips = () => {
        Modal.confirm({
            title: i18n.t('base.tipText'),
            content: i18n.t('setting.fee.changePackageTips.basics', { packageText: currentUpgradePackageText.value }),
            okText: i18n.t('base.upgradeNow'),
            cancelText: i18n.t('base.closeBtn'),
            onOk: () => {
                router.push({ name: 'fee' });
            },
        });
    };

    // 检测是否禁止支付
    function checkDisabledPay() {
        if (['52219'].includes(projectId.value)) {
            Modal.warning({
                title: '提示',
                content: '您好，系统检测到您的salesmartly项目充值进款异常，现对您的项目进款渠道进行封控，封控期间您不能往项目充值及不能购买项目等操作，请马上联系客服',
            });
            return true;
        }
        return false;
    }

    return {
        isCustomizedVersion,
        isCaiNiao,
        notNoticeIcon,
        isPackageV1,
        isPackageV2,
        isPackageV3,
        isV2PayPackage,
        packageInfo,
        checkIsExpired,
        checkExportVistor,
        checkExportSession,
        checkSessionQuality,
        showErrorTips,
        checkDataAnalysisEnabled,
        isPkgExpired,
        packageType,
        checkIsCommercialLimit,
        checkIsProLimit,
        checkIsProLimitAfterAddAccount,
        checkV3FunctionPackage,
        checkV3FunctionPackageIsLimit,
        isBasic,
        currentUpgradePackageText,
        upgradePackageTips,
        packageConfirm,
        checkDisabledPay,
        isPackageV5,
    };
}
